import React, { FunctionComponent, useEffect, useState } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardTimePicker, KeyboardTimePickerProps, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import { makeStyles } from '@material-ui/core';

type TimePickerProps = Omit<KeyboardTimePickerProps, 'value' | 'onChange'> & {
    value: string | null | undefined;
    onChange: (value: string | null) => void;
    disabled?: boolean;
};

const useStyles = makeStyles(() => ({
    default: {
        width: '100%',
        marginBottom: '5px',
        '&>.MuiOutlinedInput-root': {
            '&>.MuiOutlinedInput-input': {
                padding: 10,
            },
            '&>.MuiInputAdornment-root': {
                '&>.MuiIconButton-root': {
                    padding: 0,
                },
            },
            '&>.Mui-disabled': {
                cursor: 'not-allowed',
            },
        },
        '&>.MuiOutlinedInput-adornedEnd': {
            paddingRight: '5px',
        },
        '&>.Mui-disabled': {
            cursor: 'not-allowed',
        },
    },
}));

const TimePicker: FunctionComponent<TimePickerProps> = (props) => {
    const { value, onChange, error, className, disabled, ...other } = props;
    const [time, setTime] = useState<Date | null>(value ? moment(value, 'HH:mm:ss').toDate() : null);

    // Maintain internal state for the selected value since the MUI TimePicker expects a Date.  As far
    // as code that makes use of this component, the time picker value is a string in the format HH:mm
    // (24 hour clock).
    useEffect(() => {
        setTime(value ? moment(value, 'HH:mm:ss').toDate() : null);
    }, [value]);

    const classes = useStyles();

    const handleChange = (date: MaterialUiPickersDate) => {
        if (!date) {
            onChange(null);
        } else {
            const dateMoment = moment(date);

            if (dateMoment.isValid()) {
                onChange(dateMoment.format('HH:mm'));
            } else {
                setTime(date);
            }
        }
    };

    const hasError = error || !!(time && !moment(time).isValid());

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardTimePicker
                value={time}
                onChange={handleChange}
                error={hasError}
                inputVariant="outlined"
                className={className ?? classes.default}
                {...other}
                disabled={disabled}
            />
        </MuiPickersUtilsProvider>
    );
};

export default TimePicker;
